import { useIsMutating } from "@tanstack/react-query";
import { Eye, EyeOff, Lock } from "lucide-react";
import { useState } from "react";
import { Controller } from "react-hook-form";

import { Button } from "@/components/button";
import { FieldError, Input, Label, TextField } from "@/components/field";
import { Modal } from "@/components/modal";

import IlustrationUploadedPassword from "./components/IlustrationUpdatedPassword";
import { PasswordRequirements } from "./components/PasswordRequeriments";
import { validatePassword } from "./components/validatePassword";
import {
  type UpdatePasswordFormData,
  useUpdatePasswordForm,
} from "./hooks/useUpdatePasswordForm";
import { useUpdatePasswordMutation } from "./hooks/useUpdatePasswordMutation";

export default function ForceUpdatePasswordModal({
  mustChangePassword,
}: {
  mustChangePassword: boolean | undefined;
}) {
  const { control, handleSubmit, setError, watch } = useUpdatePasswordForm();
  const { mutate } = useUpdatePasswordMutation();
  const password = watch("password");
  const confirmPassword = watch("confirmPassword");
  const isPasswordValid = Object.values(
    validatePassword(password, confirmPassword),
  ).every(Boolean);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [showModal, setShowModal] = useState(mustChangePassword);
  const isLoading = useIsMutating({ mutationKey: ["update-password"] });

  function onSubmit(data: UpdatePasswordFormData) {
    mutate(data, {
      onError: (error) => {
        setError("confirmPassword", { message: " ", type: "manual" });
        setError("password", {
          message: error.message,
          type: "manual",
        });
      },
      onSuccess: (data) => {
        if (data) {
          setPasswordUpdated(true);
        }
      },
    });
  }

  return (
    <Modal
      classNames={{ modal: "p-10 w-120 overflow-y-auto" }}
      isOpen={showModal}
    >
      {passwordUpdated ? (
        <div className="flex items-center justify-center flex-col">
          <IlustrationUploadedPassword />
          <h2 className="font-semibold text-2xl text-text-primary mt-4">
            Password updated
          </h2>
          <span className="text-text-tertiary">
            Your password has been successfully updated!
          </span>
          <Button className="w-full mt-6" onPress={() => setShowModal(false)}>
            Access Platform
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-center flex-col">
          <div className="bg-bg-brand-primary rounded-full w-13 h-13 flex items-center justify-center mb-6">
            <Lock className="text-text-brand-primary" size={26} />
          </div>
          <p className="text-text-primary font-semibold text-center text-3xl mb-6">
            Update your password
          </p>
          <p className="text-text-secondary text-center mb-6 w-full">
            Your current password doesn’t meet our updated security
            requirements. Please create a new one to continue
          </p>
          <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col">
              <Controller
                control={control}
                name="password"
                render={({
                  field: { name, onBlur, onChange, ref, value },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    className="w-full"
                    isInvalid={invalid}
                    isRequired
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    validationBehavior="aria"
                    value={value}
                  >
                    <Label>New password</Label>
                    <div className="relative mb-6">
                      <Input
                        placeholder="Enter your password"
                        ref={ref}
                        type={showPassword ? "text" : "password"}
                      />
                      <button
                        className="absolute inset-y-0 right-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                        type="button"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                        ) : (
                          <Eye className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                        )}
                      </button>
                    </div>
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
                rules={{ required: "New password is required." }}
              />
              <PasswordRequirements
                confirmPassword={confirmPassword}
                password={password}
              />
              <Controller
                control={control}
                name="confirmPassword"
                render={({
                  field: { name, onBlur, onChange, ref, value },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    className="mt-5 w-90"
                    isInvalid={invalid}
                    isRequired
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    validationBehavior="aria"
                    value={value}
                  >
                    <Label>Confirm password</Label>
                    <div className="relative mb-6">
                      <Input
                        placeholder="Enter your password"
                        ref={ref}
                        type={showPassword ? "text" : "password"}
                      />
                      <button
                        className="absolute inset-y-0 right-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                        type="button"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                        ) : (
                          <Eye className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                        )}
                      </button>
                    </div>
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
                rules={{ required: "Confirm password is required." }}
              />
              <Button
                isDisabled={!isPasswordValid || !!isLoading}
                type="submit"
              >
                {isLoading ? "Saving your new password..." : "Update password"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </Modal>
  );
}
